import React, { useRef, useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { Button } from "@/components/ui/Button";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, Loader2, KeyRound, UserRound } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function initials(name) {
  return (name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
}

function Card({ title, description, icon: Icon, children }) {
  return (
    <section className="bg-white border border-neutral-200 rounded-2xl p-6">
      <div className="flex items-start gap-3 mb-5">
        {Icon && <div className="h-9 w-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0"><Icon className="h-4.5 w-4.5" /></div>}
        <div>
          <h2 className="font-display font-semibold text-ink text-[16px]">{title}</h2>
          {description && <p className="text-[13px] text-ink-muted mt-0.5">{description}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function LabeledInput({ label, ...props }) {
  return (
    <label className="block">
      <span className="block text-[12.5px] font-medium text-ink-muted mb-1.5">{label}</span>
      <input {...props} className="w-full h-10 px-3 rounded-lg border border-neutral-300 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand disabled:bg-neutral-50 disabled:text-ink-muted" />
    </label>
  );
}

function AvatarCard() {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = user?.avatar_url ? `${BACKEND}${user.avatar_url}` : null;

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { toast.error("Doar imagini JPG, PNG sau WEBP"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Imaginea depășește 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser((u) => ({ ...u, avatar_url: data.avatar_url }));
      toast.success("Poza de profil a fost actualizată");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  }

  return (
    <Card title="Poză de profil" description="Imaginea ta este vizibilă colegilor în platformă." icon={Camera}>
      <div className="flex items-center gap-5">
        <div className="relative h-20 w-20 rounded-full overflow-hidden bg-brand/10 text-brand flex items-center justify-center shrink-0" data-testid="account-avatar">
          {src ? <img src={src} alt={user?.name} className="h-full w-full object-cover" /> : <span className="text-[22px] font-semibold">{initials(user?.name)}</span>}
        </div>
        <div>
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="account-avatar-upload">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Încarcă poză
          </Button>
          <p className="text-[12px] text-ink-muted mt-2">JPG, PNG sau WEBP. Maxim 5MB.</p>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" />
    </Card>
  );
}

function ContactCard() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", phone: user?.phone || "", job_title: user?.job_title || "" });
  const [busy, setBusy] = useState(false);
  const dirty = f.name !== (user?.name || "") || f.phone !== (user?.phone || "") || f.job_title !== (user?.job_title || "");

  async function save(e) {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Numele nu poate fi gol"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", f);
      setUser((u) => ({ ...u, ...data }));
      toast.success("Datele au fost salvate");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  }

  return (
    <Card title="Date de contact" description="Numele și funcția sunt vizibile colegilor." icon={UserRound}>
      <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
        <LabeledInput label="Nume complet" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="account-name" />
        <LabeledInput label="Funcție" value={f.job_title} onChange={(e) => setF({ ...f, job_title: e.target.value })} placeholder="Ex.: Project Manager" data-testid="account-job" />
        <LabeledInput label="Telefon" type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+40 7xx xxx xxx" data-testid="account-phone" />
        <LabeledInput label="Email de autentificare" value={user?.email || ""} disabled />
        <div className="sm:col-span-2 flex justify-end">
          <Button type="submit" loading={busy} disabled={!dirty} data-testid="account-save">Salvează</Button>
        </div>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const [f, setF] = useState({ cur: "", np: "", np2: "" });
  const [busy, setBusy] = useState(false);

  async function save(e) {
    e.preventDefault();
    if (!f.cur) { toast.error("Introdu parola actuală"); return; }
    if (f.np.length < 8) { toast.error("Parola nouă trebuie să aibă minim 8 caractere"); return; }
    if (f.np !== f.np2) { toast.error("Parolele nu coincid"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.cur, new_password: f.np });
      setF({ cur: "", np: "", np2: "" });
      toast.success("Parola a fost schimbată");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  }

  return (
    <Card title="Schimbă parola" description="Alege o parolă pe care nu o folosești în altă parte." icon={KeyRound}>
      <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2 sm:max-w-[calc(50%-8px)]"><LabeledInput label="Parola actuală" type="password" value={f.cur} onChange={(e) => setF({ ...f, cur: e.target.value })} autoComplete="current-password" data-testid="account-pwd-current" /></div>
        <LabeledInput label="Parola nouă" type="password" value={f.np} onChange={(e) => setF({ ...f, np: e.target.value })} autoComplete="new-password" data-testid="account-pwd-new" />
        <LabeledInput label="Confirmă parola nouă" type="password" value={f.np2} onChange={(e) => setF({ ...f, np2: e.target.value })} autoComplete="new-password" data-testid="account-pwd-confirm" />
        <div className="sm:col-span-2 flex justify-end">
          <Button type="submit" variant="secondary" loading={busy} data-testid="account-pwd-save">Actualizează parola</Button>
        </div>
      </form>
    </Card>
  );
}

export default function Account() {
  return (
    <PortalLayout variant="staff">
      <div className="max-w-3xl mx-auto space-y-5" data-testid="account-page">
        <div>
          <h1 className="font-display font-semibold text-[24px] text-ink">Contul meu</h1>
          <p className="text-[14px] text-ink-muted mt-1">Actualizează-ți poza de profil, datele de contact și parola.</p>
        </div>
        <AvatarCard />
        <ContactCard />
        <PasswordCard />
      </div>
    </PortalLayout>
  );
}
